import { ArrowLeftIcon } from '@radix-ui/react-icons';

import { Button } from '@/components/ui/button';

import { CodeInput } from '@/features/auth/ui/code-input';
import { formatCooldown } from '@/shared/lib/otp-cooldown';

type CodeStepProps = {
  email: string;
  onSubmit: (code: string) => Promise<void>;
  onBack: () => void;
  onResend: () => Promise<void>;
  isVerifying: boolean;
  isCodeError: boolean;
  isResending: boolean;
  timeLeft: number;
  canResend: boolean;
};

export const CodeStep = ({
  onSubmit,
  onBack,
  onResend,
  isVerifying,
  isCodeError,
  isResending,
  timeLeft,
  canResend,
}: CodeStepProps) => {
  return (
    <div className="space-y-6">
      <div className="mb-2">
        <Button
          variant="ghost"
          onClick={onBack}
          className="h-8 gap-2 px-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>Back to email</span>
        </Button>
      </div>

      <CodeInput
        onComplete={onSubmit}
        disabled={isVerifying}
        error={isCodeError}
        onChange={() => {
          // Clear error when user starts typing new code
        }}
      />

      {isVerifying && (
        <div className="text-center">
          <p className="text-muted-foreground text-sm">Verifying your code...</p>
        </div>
      )}

      <div className="text-center space-y-2">
        {/* Pre-empt the spam-resend loop: most "missing" codes are just slow or
            in spam. Shown regardless of cooldown state. */}
        <p className="text-xs text-muted-foreground">
          Codes can take a minute to arrive — check your spam folder before requesting a new one.
        </p>

        {!canResend ? (
          // Visual-only countdown (no aria-live — a per-second polite region is
          // screen-reader noise; we announce once when resend re-opens below).
          <p className="text-sm text-muted-foreground">
            You can request a new code in{' '}
            <span className="text-primary font-medium whitespace-nowrap">
              {formatCooldown(timeLeft)}
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            Didn't get it?{' '}
            <Button
              type="button"
              variant="link"
              onClick={onResend}
              disabled={isResending}
              className="text-primary hover:text-primary/80 underline disabled:opacity-50 h-auto p-0 font-normal align-baseline"
            >
              {isResending ? 'Sending...' : 'Resend code'}
            </Button>
          </p>
        )}
      </div>
    </div>
  );
};
